<nav class="sticky top-0 z-40 h-14 sm:h-16 w-full border-b border-border bg-surface shadow-xs">
  <div class="mx-auto flex h-full max-w-7xl items-center justify-between gap-2 px-3 sm:px-4">
    <!-- Left: Back Button + Title -->
    <div class="flex items-center gap-1.5 sm:gap-2.5 min-w-0 flex-1">
      @if (canGoBack()) {
      <button (click)="goBack()"
        class="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full text-muted hover:text-main hover:bg-surface-hover active:bg-surface-hover/80 focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer transition-colors"
        aria-label="Go back">
        <svg class="h-5 w-5 sm:h-6 sm:w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>
      }

      <div class="min-w-0 truncate">
        <span class="truncate block text-sm sm:text-base font-bold text-main tracking-tight">
          {{ this.settingsService.customer?.Name }}
        </span>
      </div>
    </div>

    <!-- Right Container: Active Order Status Pill, Cart Button, Language Selector -->
    <div class="flex items-center justify-end gap-1.5 sm:gap-2.5 shrink-0">
      <!-- Live Order Status Pill (Streamlined for space) -->
      @if (activeOrderCount() > 0) {
      <button type="button" (click)="openTracking()"
        class="flex items-center gap-1.5 py-1 px-2 sm:px-2.5 rounded-full text-xs font-semibold transition-all shadow-2xs cursor-pointer active:scale-95 shrink-0" [ngClass]="{
            'bg-amber-100 text-amber-900 border border-amber-300': topOrderStatus() === OnlineOrderStatus.PENDING,
            'bg-emerald-100 text-emerald-900 border border-emerald-300': topOrderStatus() === OnlineOrderStatus.ACCEPTED || topOrderStatus() === OnlineOrderStatus.PREPARING,
            'bg-green-100 text-green-900 border border-green-300': topOrderStatus() === OnlineOrderStatus.READY,
            'bg-red-100 text-red-900 border border-red-300': topOrderStatus() === OnlineOrderStatus.REJECTED
          }" [title]="'TRACKING.TITLE' | translate">
        <!-- Status Indicator Dot -->
        @if (topOrderStatus() === OnlineOrderStatus.PENDING) {
        <span class="relative flex h-2 w-2 shrink-0">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
        </span>
        } @else if (topOrderStatus() === OnlineOrderStatus.ACCEPTED || topOrderStatus() === OnlineOrderStatus.PREPARING) {
        <span class="relative flex h-2 w-2 shrink-0">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        } @else if (topOrderStatus() === OnlineOrderStatus.READY) {
        <span class="w-2 h-2 rounded-full bg-green-600 shrink-0"></span>
        } @else if (topOrderStatus() === OnlineOrderStatus.REJECTED) {
        <span class="w-2 h-2 rounded-full bg-red-500 shrink-0"></span>
        }

        <!-- Status Icon -->
        @if (topOrderStatus() === OnlineOrderStatus.ACCEPTED || topOrderStatus() === OnlineOrderStatus.PREPARING) {
        <!-- Kitchen Pot / Chef Pan Cooking Icon -->
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48Z" />
        </svg>
        } @else if (topOrderStatus() === OnlineOrderStatus.READY) {
        <!-- Bell / Ready Icon -->
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
        </svg>
        } @else if (topOrderStatus() === OnlineOrderStatus.PENDING) {
        <!-- Clock / Pending Icon -->
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        } @else if (topOrderStatus() === OnlineOrderStatus.REJECTED) {
        <!-- Warning Icon -->
        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
        }

        <!-- Text Label (Shown on sm+ screens, hidden on compact mobile to give Customer.Name maximum room) -->
        @if (activeOrderCount() === 1) {
        @if (topOrderStatus() === OnlineOrderStatus.PENDING) {
        <span class="hidden sm:inline truncate text-[11px] sm:text-xs">{{ 'TRACKING.PENDING_BADGE' | translate }}</span>
        } @else if (topOrderStatus() === OnlineOrderStatus.ACCEPTED || topOrderStatus() === OnlineOrderStatus.PREPARING) {
        <span class="hidden sm:inline truncate text-[11px] sm:text-xs">{{ 'TRACKING.ACCEPTED_BADGE' | translate }}</span>
        } @else if (topOrderStatus() === OnlineOrderStatus.READY) {
        <span class="hidden sm:inline truncate text-[11px] sm:text-xs">{{ 'TRACKING.READY_BADGE' | translate }}</span>
        } @else if (topOrderStatus() === OnlineOrderStatus.REJECTED) {
        <span class="hidden sm:inline truncate text-[11px] sm:text-xs">{{ 'TRACKING.REJECTED_BADGE' | translate }}</span>
        }
        } @else {
        <!-- Multi-Order Count -->
        <span class="text-[11px] font-bold">{{ activeOrderCount() }}</span>
        <span class="hidden sm:inline text-xs">{{ 'TRACKING.ORDERS_SHORT' | translate }}</span>
        }
      </button>
      }

      <!-- Cart Button with Counter Badge -->
      @if (settingsService.isOrderingEnabled()) {
      <button type="button" (click)="openCart()"
        class="relative flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full text-main hover:bg-surface-hover active:bg-surface-hover/80 focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer transition-all duration-200"
        [class.animate-cart-bump]="isCartBumping()" [class.text-primary]="isCartBumping()" [class.bg-primary/10]="isCartBumping()" aria-label="Cart">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5 sm:w-6 sm:h-6 transition-transform">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
        </svg>

        @if (cartService.itemCount() > 0) {
        <span
          class="absolute -top-0.5 -right-0.5 flex h-4.5 min-w-4.5 sm:h-5 sm:min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] sm:text-[11px] font-bold text-primary-foreground shadow-xs transition-transform"
          [class.animate-badge-pop]="isCartBumping()">
          {{ cartService.itemCount() }}
        </span>
        }
      </button>
      }

      <!-- Language Selector -->
      @if ( settingsService.settingsLoaded() && settingsService.availableLanguages().length > 1 ) {
      <app-language-selector [availableLanguages]="settingsService.availableLanguages()" [activeMenuLanguage]="settingsService.activeMenuLanguage()"
        (languageChange)="onLanguageChange($event)"></app-language-selector>
      }
    </div>
  </div>
</nav>